/**
 * Firebase Cloud Messaging (FCM) — web push setup for Link.
 *
 * Coexists with Link's existing PWA service worker (/service-worker.js).
 * Firebase requires its OWN service worker at /firebase-messaging-sw.js
 * for background notifications; both workers register independently and
 * do not interfere — `firebase/messaging` automatically picks up the
 * messaging-specific worker via its registration scope.
 *
 * The public app config + VAPID key live in /app/frontend/.env.  The
 * service account / Firebase Admin private key is BACKEND-ONLY and
 * never ships with the bundle.
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let _app = null;
let _messaging = null;

/** Lazy app singleton.  React StrictMode mounts effects twice; getApps()
 *  prevents duplicate-app errors. */
export function getFirebaseApp() {
  if (!firebaseConfig.apiKey) return null;
  if (_app) return _app;
  _app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  return _app;
}

/** Returns a Messaging instance only when the browser actually supports
 *  FCM (rules out private-mode Safari, in-app browsers, older WebViews,
 *  iOS PWAs pre-16.4).  Callers should treat null as "no push here". */
export async function getMessagingSafe() {
  if (_messaging) return _messaging;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    const app = getFirebaseApp();
    if (!app) return null;
    _messaging = getMessaging(app);
    return _messaging;
  } catch (e) {
    console.warn("[fcm] messaging unsupported:", e?.message || e);
    return null;
  }
}

/**
 * Register the dedicated firebase-messaging-sw.js worker (separate from
 * the app's existing /service-worker.js cache worker) and request a
 * device token.  Returns the FCM token string or null on failure.
 *
 *   – Idempotent: re-calling returns the cached token unless `force`.
 *   – Caller is responsible for posting the token to the backend
 *     (POST /api/notifications/register-token).
 *
 * Logs every failure mode to the console so the rider can paste the
 * stack into a bug report instead of staring at a silent UI.
 */
export async function requestPushPermissionAndToken({ force = false } = {}) {
  if (typeof window === "undefined") return null;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    console.warn("[fcm] browser lacks Notification or serviceWorker APIs");
    return null;
  }

  const messaging = await getMessagingSafe();
  if (!messaging) {
    console.warn("[fcm] getMessagingSafe returned null — likely unsupported environment");
    return null;
  }

  if (!force) {
    const cached = localStorage.getItem("fcm_token");
    if (cached) return cached;
  }

  let permission = Notification.permission;
  if (permission === "default") permission = await Notification.requestPermission();
  if (permission !== "granted") {
    console.warn("[fcm] permission not granted:", permission);
    return null;
  }

  // Wait for our own messaging worker to be ready.  If it isn't
  // registered yet, do it explicitly with the correct path AND wait
  // for activation before attempting getToken — otherwise FCM throws
  // "messaging/failed-service-worker-registration".
  let registration = null;
  try {
    registration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (!registration) {
      registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
        scope: "/firebase-cloud-messaging-push-scope",
      });
    }
    // Critical: getToken throws if the SW isn't yet activated.
    if (registration.installing || registration.waiting) {
      await new Promise((resolve) => {
        const w = registration.installing || registration.waiting;
        if (!w) return resolve();
        w.addEventListener("statechange", () => {
          if (w.state === "activated") resolve();
        });
        // Hard ceiling — don't hang the UI forever.
        setTimeout(resolve, 4000);
      });
    }
  } catch (e) {
    console.error("[fcm] worker register/activate failed:", e?.message || e);
    return null;
  }

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.error("[fcm] REACT_APP_FIREBASE_VAPID_KEY is undefined — bundle was built before .env update; restart the dev server.");
    return null;
  }
  try {
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      localStorage.setItem("fcm_token", token);
      console.log("[fcm] obtained token (tail):", "…" + token.slice(-12));
    } else {
      console.warn("[fcm] getToken returned empty — permission may have just been revoked");
    }
    return token || null;
  } catch (e) {
    console.error("[fcm] getToken failed:", e?.code || "", e?.message || e);
    return null;
  }
}

/** Subscribe to foreground push payloads.  Returns the unsubscribe fn. */
export async function onForegroundPush(handler) {
  const messaging = await getMessagingSafe();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
